import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { applyWriteMap } from "../firebase/writes.js";
import {
  type CustomizationRecord,
  addCustomSkill,
  clearSkillRename,
  markSkillRemoved,
  removeAddedSkill,
  renameSkill,
  unmarkSkillRemoved,
} from "../lib/customization-edits.js";

interface Props {
  tablePath: string;
  customization: CustomizationRecord;
  /** Skill ids defined by the pack — used to show remove toggles. */
  packSkillIds: string[];
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export default function CustomizationSkillsEditor({
  tablePath,
  customization,
  packSkillIds,
}: Props): ReactElement {
  const { t } = useTranslation();
  const [newName, setNewName] = useState("");

  async function write(writes: Record<string, unknown>): Promise<void> {
    const { db } = getFirebase();
    await applyWriteMap(db, writes);
  }

  async function handleAdd(e: FormEvent): Promise<void> {
    e.preventDefault();
    const id = slug(newName);
    if (!id) return;
    await write(
      addCustomSkill(tablePath, customization, {
        id,
        nameKey: `custom.skills.${id}.name`,
        descKey: `custom.skills.${id}.desc`,
        usage: ["overcome", "create-advantage"],
      }),
    );
    setNewName("");
  }

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold uppercase text-muted-foreground">
          {t("customization.skills.added")}
        </h3>
        <ul className="mt-1 space-y-1">
          {(customization.skills.added ?? []).map((s) => (
            <li key={s.id} className="flex items-center gap-2 text-sm">
              <span className="flex-1 font-mono">{s.id}</span>
              <button
                type="button"
                className="rounded border border-rose-400 px-2 py-0.5 text-xs text-rose-700"
                onClick={() => write(removeAddedSkill(tablePath, customization, s.id))}
              >
                {t("edit.delete")}
              </button>
            </li>
          ))}
        </ul>
        <form onSubmit={handleAdd} className="mt-2 flex gap-2 text-sm">
          <input
            className="flex-1 rounded border px-2 py-1"
            placeholder={t("customization.skills.new-placeholder")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            aria-label={t("customization.skills.new")}
          />
          <button type="submit" className="rounded border px-2 py-1">
            {t("edit.save")}
          </button>
        </form>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase text-muted-foreground">
          {t("customization.skills.removed")}
        </h3>
        <ul className="mt-1 space-y-1 text-sm">
          {packSkillIds.map((id) => {
            const removed = (customization.skills.removed ?? []).includes(id);
            return (
              <li key={id} className="flex items-center gap-2">
                <label className="flex flex-1 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={removed}
                    onChange={(e) =>
                      write(
                        e.target.checked
                          ? markSkillRemoved(tablePath, customization, id)
                          : unmarkSkillRemoved(tablePath, customization, id),
                      )
                    }
                    aria-label={`${t("customization.skills.remove-toggle")} ${id}`}
                  />
                  <span className="font-mono">{id}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase text-muted-foreground">
          {t("customization.skills.renamed")}
        </h3>
        <ul className="mt-1 space-y-1 text-sm">
          {packSkillIds.map((id) => {
            const current = customization.skills.renamed?.[id];
            return (
              <li key={id} className="flex items-center gap-2">
                <span className="w-24 font-mono">{id}</span>
                <input
                  className="flex-1 rounded border px-2 py-1"
                  placeholder={t("customization.skills.rename-placeholder")}
                  defaultValue={current?.nameKey ?? ""}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v.length === 0) {
                      void write(clearSkillRename(tablePath, id));
                    } else {
                      void write(renameSkill(tablePath, customization, id, { nameKey: v }));
                    }
                  }}
                  aria-label={`${t("customization.skills.rename-input")} ${id}`}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
